import { useEffect, useRef, useState } from "react"
import { FaceDetection } from "@mediapipe/face_detection"

/**
 * Camera framing — runs MediaPipe FaceDetection over a live `MediaStream`
 * and emits a normalised `faceOffset` that the lobby preview can convert
 * into a CSS `object-position`. The result keeps the user roughly centered
 * even if they're sitting off to the side of the frame.
 *
 * Activation is fully optional: when `enabled` is false the hook does
 * nothing and returns the zero offset. The mediapipe model is only loaded
 * once `enabled` becomes true to keep startup cheap.
 *
 *   offset.x / offset.y ∈ [-25, 25]  → percentage to shift `object-position`
 *   from the default 50%, 50%. Clamped so the user is never pulled
 *   completely off-frame even if detection drifts.
 */

const CDN_BASE =
  "https://cdn.jsdelivr.net/npm/@mediapipe/face_detection@0.4.1646425229"
const TICK_MS = 250 // 4 detections/sec — plenty for smooth framing.
const MAX_OFFSET_PCT = 25
const SMOOTHING = 0.2 // 0 = instant, 1 = never moves; tuned by feel.

export interface FaceOffset {
  x: number
  y: number
}

export function useFaceFraming(stream: MediaStream | null, enabled: boolean): FaceOffset {
  const [offset, setOffset] = useState<FaceOffset>({ x: 0, y: 0 })

  const videoRef = useRef<HTMLVideoElement | null>(null)
  const detectionRef = useRef<FaceDetection | null>(null)
  const tickRef = useRef<number | null>(null)
  const targetRef = useRef<FaceOffset>({ x: 0, y: 0 })
  const rafRef = useRef<number | null>(null)
  const offsetRef = useRef<FaceOffset>({ x: 0, y: 0 })

  // Cleanup any in-flight work on disable / unmount / stream change.
  useEffect(() => {
    if (!enabled || !stream) {
      targetRef.current = { x: 0, y: 0 }
      setOffset({ x: 0, y: 0 })
      offsetRef.current = { x: 0, y: 0 }
      if (tickRef.current) {
        window.clearInterval(tickRef.current)
        tickRef.current = null
      }
      if (rafRef.current) {
        window.cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      try {
        detectionRef.current?.close?.()
      } catch {
        /* noop */
      }
      detectionRef.current = null
      if (videoRef.current) {
        videoRef.current.srcObject = null
        videoRef.current = null
      }
      return
    }

    let cancelled = false

    // Hidden video element drives the mediapipe input — we don't render it.
    const video = document.createElement("video")
    video.muted = true
    video.playsInline = true
    video.srcObject = stream
    void video.play().catch(() => {})
    videoRef.current = video

    const detection = new FaceDetection({
      locateFile: (file: string) => `${CDN_BASE}/${file}`,
    })
    detection.setOptions({ model: "short", minDetectionConfidence: 0.5 })
    detection.onResults((results) => {
      if (cancelled) return
      const det = results.detections?.[0]
      if (!det) return
      // boundingBox is normalised: xCenter/yCenter/width/height ∈ [0,1].
      const box = det.boundingBox
      if (!box) return
      // Shift = how far off-center the face is, in image-space units (-0.5..0.5).
      // Multiply by 100 to get percent; clamp.
      const xPct = (box.xCenter - 0.5) * 100
      const yPct = (box.yCenter - 0.5) * 100
      targetRef.current = {
        x: clamp(xPct, -MAX_OFFSET_PCT, MAX_OFFSET_PCT),
        y: clamp(yPct, -MAX_OFFSET_PCT, MAX_OFFSET_PCT),
      }
    })
    detectionRef.current = detection

    const tick = async () => {
      if (cancelled) return
      if (video.readyState < 2 || video.videoWidth === 0) return
      try {
        await detection.send({ image: video })
      } catch {
        // mediapipe occasionally throws while the worker is starting up.
      }
    }

    // Animation loop smooths the latest target into the displayed offset so
    // movement looks like a slow pan rather than a jitter.
    const smooth = () => {
      const cur = offsetRef.current
      const target = targetRef.current
      const next = {
        x: cur.x + (target.x - cur.x) * SMOOTHING,
        y: cur.y + (target.y - cur.y) * SMOOTHING,
      }
      offsetRef.current = next
      // Only re-render when the change is perceptible (≥ 0.5% drift).
      if (Math.abs(next.x - cur.x) >= 0.1 || Math.abs(next.y - cur.y) >= 0.1) {
        setOffset(next)
      }
      rafRef.current = window.requestAnimationFrame(smooth)
    }

    tickRef.current = window.setInterval(tick, TICK_MS)
    rafRef.current = window.requestAnimationFrame(smooth)

    return () => {
      cancelled = true
      if (tickRef.current) window.clearInterval(tickRef.current)
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current)
      try {
        detection.close?.()
      } catch {
        /* noop */
      }
      video.srcObject = null
      videoRef.current = null
    }
  }, [enabled, stream])

  return offset
}

function clamp(value: number, min: number, max: number): number {
  if (value < min) return min
  if (value > max) return max
  return value
}
